// Tulevaisuustestaus v2 (#1474), the `futureV2` namespace in English; split by language from
// futureReimaginedV2Strings.js (the Finnish, and the file's notes) when it neared 1000 lines.

export const futureReimaginedV2En = {
  loading: "Loading solutions…",
  loadError: "Loading failed: {{message}}",
  tooFew: "Interplay can be read once Solutions has at least two assessed solutions. There are {{count}} now.",
  unassessed: "These solutions have not been assessed yet, so they are missing from the comparison: {{names}}",
  offMap: "Written on vipupisteet the Ongelmakartta no longer shows (the map was run again): {{names}}. Their moves are missing from the interplay and the test; assess them again in Ratkaisut.",
  views: {
    select: "Selection",
    analysis: "Analysis",
    interplay: "Interplay",
    timeline: "Timeline",
    fates: "Fates",
    vipupisteet: "Leverage points",
    compare: "Comparison",
    needsCourse: "Analysis opens once the locked set of solutions has been tested in the scenarios.",
    courseStatus: {
      beforeLock: "Analysis opens once the solutions are locked and the facilitator has tested them in the scenarios.",
      waiting: "The solutions are locked. Analysis opens once the facilitator has tested them in the scenarios.",
      running: "The solutions are being tested in the scenarios… Analysis opens when the test is ready.",
      failed: "Imagining the future failed. The facilitator can start it again."
    },
  },
  stageBar: { label: "Stages", interplay: "Interplay", voting: "Vote", locked: "Lock", simulated: "Future testing" },
  move: { as_intended: "Moves it its way", against: "Moves it against its way" },
  matrix: {
    title: "What each solution moves",
    hint: "Rows are solutions, columns leverage points. The arrow is from the solution's assessment: whether it moves the leverage point in its own direction of change or against it.",
    sideEffect: "Side effect (not aimed at)",
    untouched: "no solution moves it",
  },
  candidates: {
    title: "Possible interplay",
    hint: "Computed from the solutions' assessments, leverage points and pathways: what two solutions have in common. It does not yet say whether they support or compete with each other.",
    none: "The solutions share no leverage points or actors.",
    all: "Show all {{count}} pairs",
    fewer: "Show fewer",
  },
  signal: {
    similarity: "Move the same leverage points {{percent}}%",
    opposed: "Opposed: {{name}}",
    same_harm: "Both move it against its way: {{name}}",
    same_good: "Both move: {{name}}",
    shared_target: "Written on the same leverage point: {{name}}",
    shared_actor: "Same actor: {{name}} ({{horizon}})",
  },
  interplay: {
    title: "Interplay",
    hintUnread: "Below is what the solutions have in common by the numbers. The AI reads the pairs and says whether the solutions overlap, compete, conflict, enable or reinforce each other — or do not affect each other at all.",
    hintRead: "The lines are the AI's reading of how the solutions affect each other. Select a solution to see only its relations. Each relation states its basis and uncertainty.",
    waiting: "The AI has not read the pairs yet. The facilitator starts the reading, and the result appears here when it is ready.",
    readAt: "Kuutio's reading, {{time}}",
    stale: "Solutions have been changed or added since this reading ({{names}}). The facilitator can read the interplay again before the vote.",
  },
  graph: { label: "Relations between the solutions" },
  relation: { conflict: "Conflict", overlap: "Overlap", competing: "Competing", enables: "Enables", reinforces: "Reinforces", independent: "Independent" },
  strength: { low: "Minor", medium: "Moderate", high: "Major" },
  basis: { research: "research", reasoned: "reasoning" },
  cluster: { overlap: "Overlapping group:", competing: "Competing group:", dependency: "Resting on one:" },
  relationCard: {
    why: "Why: the reasoning, the uncertainty and the basis",
    uncertainty: "Uncertainty: {{text}}",
    via: "Runs through: {{list}}",
    basis: "Basis: {{basis}}",
    noneDrawn: "The AI found no overlap, competition, conflict or dependency between the pairs.",
    showIndependent: "Show pairs that do not affect each other ({{count}})",
    hideIndependent: "Hide pairs that do not affect each other ({{count}})",
    unanswered: "The AI gave no verdict for these pairs: {{pairs}}",
    ragUnavailable: "The research material could not be reached, so the relations rest on reasoning.",
  },
  sources: { fold: "Sources ({{count}})", unavailable: "The sources could not be fetched this time." },
  vote: {
    title: "Vote: which solutions to keep",
    hint: "Choose the solutions to carry into future testing. Each card shows what the interplay reading connects it to. Tap again to take a vote back.",
    left: "Votes left {{left}} / {{budget}}",
    noIdentity: "This device cannot vote. Sign in with the workshop PIN.",
    vote: "Vote: {{name}}",
    unvote: "Take back vote: {{name}}",
    warning: {
      conflict: "Conflicts with: {{name}}",
      overlap: "Overlaps: {{name}}",
      competing: "Competes with: {{name}}",
      needs: "Needs first: {{name}}",
      enables: "Enables: {{name}}",
      reinforces: "Reinforces: {{name}}"
    },
    error: {
      CLOSED: "Voting is closed.",
      BUDGET: "You have no votes left. Take one back to vote for another.",
      NO_SOLUTION: "That solution no longer exists.",
      NO_VOTER: "This device was not recognised as a voter.",
      generic: "Saving the vote failed: {{message}}"
    }
  },
  locked: {
    title: "Carried into future testing",
    votes: "(votes: {{count}})",
    override: "The choice differs from the vote. The facilitator's reason: {{reason}}",
    stale: "These solutions were edited after the lock: {{names}}. The imagined timeline rests on the locked version.",
    deleted: "(deleted solution)"
  },
  admin: {
    title: "Future testing: vote and lock",
    loadError: "Loading failed: {{message}}",
    interplayRead: "The interplay has been read.",
    interplayStale: "The interplay has been read, but solutions have changed since. Read it again before the vote with Assess the interplay.",
    interplayMissing: "The interplay has not been read yet. It is worth reading before the vote with Assess the interplay.",
    votes: "Voting {{open}}, {{budget}} votes per device. Open and close voting with the switch above.",
    open: "open",
    closed: "closed",
    tension: {
      overlap: "Overlap in the selection: {{a}} and {{b}}",
      conflict: "Conflict in the selection: {{a}} and {{b}}. You can still lock; the timeline will show what it costs."
    },
    overrideHint: "The selection leaves out a solution that got more votes than one chosen. Record why: the room sees it and the report keeps it.",
    overridePlaceholder: "Why does the selection differ from the vote?",
    lock: "Lock {{count}} solutions",
    relock: "Lock again ({{count}})",
    lockFailed: "Locking failed: {{message}}",
    openModule: "Open the module",
    currentLock: "Current lock ({{time}}): {{names}}"
  },
  howWeGotHere: "How we got here: the interplay",
  actor: {
    label: "Actor",
    all: "Everyone",
    hint: "Pick your organisation: its steps stand out on the timeline. The choice is remembered on this device.",
    count: "{{name}}: {{count}} steps in this course",
    none: "{{name}} does not act in this course."
  },
  compare: {
    title: "The scenarios side by side",
    solution: "Solution",
    set: "The whole set",
    robustness: "Robustness",
    lines: "The timelines on one year axis",
    tooFew: "There is something to compare once at least two scenarios have been imagined."
  },
  course: {
    causes: "Reasons",
    title: "Imagined course: what could happen to the solutions",
    running: "The timeline is being imagined…",
    failed: "Imagining the timeline failed: {{message}}",
    yearLikelihood: "{{year}} · {{likelihood}}",
    triggers: "What causes it:",
    setAside: "Threats judged not to materialise:",
    threat: "{{what}} ({{when}}, {{likelihood}})",
    relation: "{{relation}}: {{a}} – {{b}}",
    wouldChange: "What would change this: {{text}}",
    missing: "No grounded fate came back for these solutions: {{names}}",
    until: "until {{year}}",
    after: "only once {{name}} is under way"
  },
  effect: {
    title: "Effectiveness: how far the solutions deal with the issue",
    intro: "An assessment of this course. The server works out which of the leverage points' root causes each solution reaches, and lowers a verdict that goes further than that.",
    verdict: {
      marginal: "Barely moves the issue",
      eases: "Eases the issue",
      fixes: "Fixes the issue",
      dissolves: "Dissolves the issue's premise"
    },
    short: {
      marginal: "Barely moves it",
      eases: "Eases",
      fixes: "Fixes",
      dissolves: "Dissolves it"
    },
    legend: {
      marginal: "the solution reaches few or none of the root causes, or does too little in this course",
      eases: "the issue becomes smaller or milder, but much of the root causes stay",
      fixes: "the main root causes are dealt with and the problem is largely gone, though the system around it is as before",
      dissolves: "a paradigm shift: the root causes are removed and the issue's premise is gone"
    },
    set: "The whole set of solutions",
    perSolution: "Each solution",
    reached: "Reaches root causes: {{names}}",
    reachedNone: "Reaches none of the root causes the leverage points carry.",
    coverage: "{{reached}} of {{total}} root causes",
    remaining: "Root causes no solution carried out reaches: {{names}}",
    premiseGone: "Why the issue's premise is gone: {{text}}",
    lowered: "The AI judged: {{from}}. From the map, at most: {{to}} ({{reason}}).",
    loweredReason: {
      reaches_none: "the solution reaches no root cause",
      reaches_under_half: "it reaches under half of the root causes",
      reaches_under_most: "it reaches under four fifths of the root causes",
      premise_not_argued: "it did not say why the premise is gone",
      no_root_causes: "the leverage points carry no root causes to compare with"
    },
    unjudged: "The AI did not assess this solution.",
    newIssues: "New issues carrying them out could create",
    newIssuesHint: "Reasoning, not findings: they rest on the solutions' own effects and have no source.",
    followsFrom: "Follows from: {{names}}",
    grounded: {
      adverse_move: "Rests on the solution moving a leverage point against its direction",
      world_shift: "Rests on a development the solution moves",
      reasoned: "Reasoning only"
    },
    failed: "The effectiveness assessment failed: {{message}}",
    missing: "The effectiveness of this course has not been assessed yet.",
    wall: "Effectiveness: {{verdict}}"
  },
  fate: {
    realised: "Realised",
    delayed: "Delayed",
    diminished: "Diminished",
    failed: "Fails"
  },
  impact: {
    title: "The scenario and what it does",
    show: "Show the scenario's text",
    hide: "Hide the text"
  },
  likelihood: {
    low: "unlikely",
    medium: "possible",
    high: "likely"
  },
  line: {
    scrollHint: "Swipe sideways →",
    step: "{{from}}–{{to}} · {{solution}}: {{step}}",
    stepActor: "Actor: {{actor}}",
    stepPrecondition: "Precondition: {{text}}",
    close: "Close"
  },
  narrativeKind: {
    milestone: "Milestone",
    side_effect: "Side effect",
    setback: "Setback",
    turning_point: "Turning point",
    interaction: "Interaction"
  },
  panels: {
    stepCount: "{{count}} steps",
    highlights: "What people would notice along the way",
    actions: "Actions by year",
    actionsFor: "Actions by year · {{name}} first",
    noActions: "The course has no scheduled steps."
  },
  reading: {
    title: "What the scenario does to the solutions' threats",
    pressure: {
      stronger: "strengthens",
      as_is: "leaves as is",
      weaker: "weakens",
      removed: "removes"
    },
    timing: {
      earlier: "earlier",
      same: "",
      later: "later"
    },
    kind: {
      trigger: "caused the fate",
      setAside: "threat set aside"
    },
    behind: "In the scenario: {{text}}",
    none: "The scenario does not change the threats behind this course's fates.",
    differs: "What differs between the scenarios",
    differsIntro: "The threats and relations the scenarios bear on differently, with the passage each judgement rests on. All the scenarios were read at once, side by side.",
    noDiffer: "The scenarios do not differ on the locked solutions' threats.",
    notRead: "The scenarios have not been read yet.",
    failed: "Reading the scenarios failed: {{message}}"
  },
  robustness: {
    title: "Robustness: how the solutions fare across the courses",
    verdict: "Summary",
    holds: "Holds in every course",
    failsAll: "Fails in every course",
    fails: "Fails in some course",
    diminishedAll: "Does less in every course",
    varies: "Varies by course",
    unknown: "No information"
  },
  scenario: {
    label: "Scenario",
    sources: {
      expert: "Expert scenarios",
      workshop: "The workshop's scenarios",
      own: "Own scenarios"
    },
    placeholder: "Placeholder: the experts' scenarios will replace these texts.",
    stale: "The scenario has changed since this course: imagine it again.",
    read: "Read the scenario",
    close: "Close",
    level: "Optimism level {{level}}",
    judged: "The solutions are judged realistically in this world: neither leniently nor harshly."
  },
  timeline: {
    label: "The imagined implementation of the locked solutions, year by year",
    today: "Today",
    haze: "The forecast grows less certain the further into the future it reaches",
    noNarrative: "The narrative could not be written; the course and the fates are still below. {{message}}"
  },
  worldAdmin: {
    title: "Scenarios",
    source: "Source: {{source}}, {{count}} scenarios. The source can be changed under Sisältö (Forecast scenarios).",
    none: "The chosen source has {{count}} scenarios; at least {{min}} are needed. Change the source or add scenarios under Sisältö.",
    simulate: "Imagine every scenario",
    simulating: "Imagining…",
    rerun: "Imagine again",
    analyse: "Assess effectiveness again",
    analysing: "Assessing…",
    readingStatus: {
      running: "being read",
      done: "read",
      failed: "reading failed"
    },
    status: {
      running: "being imagined",
      done: "done",
      failed: "failed"
    }
  },
  lockedStrip: {
    label: "Locked",
    override: "The choice differs from the vote",
    stale: "changed since the lock"
  },
  vipupisteet: {
    title: "Which leverage points the set moves when things go wrong",
    hint: "For each leverage point the locked solutions move: which solutions move it its way (↑) or against it (↓), and what becomes of them in each scenario. No figures are estimated: only whether the move happens.",
    type: { konsensus: "Shared view", kiistanalainen: "Contested", villi_kortti: "Wild card", sokea_piste: "Blind spot" },
    holds: "Moves its way in {{n}} of {{total}} scenarios",
    weakened: "weaker in {{n}}",
    against: "against its way in {{n}}",
    solution: "Solution",
    noFate: "no fate",
    none: "The locked solutions move none of the shown leverage points.",
  },
  wall: { title: "Future testing", intro: "Each solution has been assessed on its own, by leverage point. This step looks at what they do to each other, chooses which to keep, and imagines how the chosen set could play out. It is a vision, not a forecast: uncertainties are stated openly.", vision: "A vision, not a forecast: every moment rests on the solutions' own threats and the relations between them, and its uncertainty is stated." },
};
